import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
// import "@minohealth/common/index.css"
import "./index.css"
import App from './App.jsx'
import { Store, PersistedStore } from "@minohealth/common/services/DataStoreService/store/Store";
import * as Sentry from "@sentry/react";
import { CircleAlert } from "lucide-react";
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription, EmptyContent } from "@/components/ui/empty";
import { Button } from "@/components/ui/button";

function ErrorFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <Empty className="border-none">
        <EmptyHeader>
          <EmptyMedia>
            <CircleAlert className="size-12 text-muted-foreground" />
          </EmptyMedia>
          <EmptyTitle className="text-2xl">Something went wrong</EmptyTitle>
          <EmptyDescription className="text-base">
            An unexpected error occurred. Please try reloading the page.
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button onClick={() => window.location.reload()}>Reload page</Button>
        </EmptyContent>
      </Empty>
    </div>
  )
}


Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE || 'production',
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration({
      // Enable automatic instrumentation of browser performance
      tracePropagationTargets: [/.*/]
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
    Sentry.breadcrumbsIntegration({
      // Disable console breadcrumbs as requested
      console: false,
      // Enable DOM event breadcrumbs (clicks, inputs, etc)
      dom: true,
      // Enable fetch/XHR breadcrumbs
      fetch: true,
      // Enable navigation breadcrumbs (URL changes, back/forward)
      history: true,
      // Enable global error/unhandled rejection breadcrumbs
      sentry: true,
      xhr: true,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
  // Session Replay
  replaysSessionSampleRate: 0.1, // 10% of sessions
  replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

  // Before sending events, add extra context
  beforeSend(event, hint) {
    // Add browser info
    if (event.contexts) {
      event.contexts.browser_info = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        online: navigator.onLine,
        cookieEnabled: navigator.cookieEnabled,
      };

      // Add screen info
      event.contexts.screen = {
        width: window.screen.width,
        height: window.screen.height,
        availWidth: window.screen.availWidth,
        availHeight: window.screen.availHeight,
        colorDepth: window.screen.colorDepth,
      };

      // Add viewport info
      event.contexts.viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
    }

    return event;
  },

  // Before sending breadcrumbs, add extra context
  beforeBreadcrumb(breadcrumb, hint) {
    // Enrich HTTP breadcrumbs with more details
    if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
      // Add timestamp
      breadcrumb.timestamp = Date.now() / 1000;

      // Try to parse response data if available
      if (hint?.response) {
        try {
          breadcrumb.data = breadcrumb.data || {};
          breadcrumb.data.responseStatus = hint.response.status;
          breadcrumb.data.responseStatusText = hint.response.statusText;
        } catch (e) {
          // Ignore parsing errors
        }
      }
    }

    return breadcrumb;
  },
});

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <Provider store={Store}>
      <PersistGate loading={null} persistor={PersistedStore}>
        <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
          <App />
        </Sentry.ErrorBoundary>
      </PersistGate>
    </Provider>
  </StrictMode>,
)
